import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateLearningSpace } from '@/features/learning-spaces/hooks';
import type { LearningSpaceView } from '@/features/learning-spaces/types';
import { ApiError } from '@/lib/api/client';

export function EditLearningSpaceDialog({
  learningSpace,
  open,
  onOpenChange,
}: {
  readonly learningSpace: LearningSpaceView;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [name, setName] = useState(learningSpace.name);
  const [description, setDescription] = useState(learningSpace.description ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const update = useUpdateLearningSpace(learningSpace.id);

  useEffect(() => {
    if (open) {
      setName(learningSpace.name);
      setDescription(learningSpace.description ?? '');
      setValidationError(null);
      update.reset();
    }
    // Only re-sync when the dialog opens for this learning space, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, learningSpace.id]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (name.trim().length === 0) {
      setValidationError('Nama ruang belajar tidak boleh kosong.');
      return;
    }

    update.mutate(
      {
        name: name.trim(),
        description: description.trim().length > 0 ? description.trim() : null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    update.error instanceof ApiError
      ? update.error.message
      : update.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ubah Ruang Belajar</DialogTitle>
          <DialogDescription>Perbarui nama atau deskripsi ruang belajar ini.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-learning-space-name">Nama</Label>
            <Input
              id="edit-learning-space-name"
              type="text"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-learning-space-description">Deskripsi</Label>
            <Textarea
              id="edit-learning-space-description"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
            />
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
